// Just a single number here, since we default to a square image
const DEFAULT_SIZE = 300

// loadImage creates a new image object and attempts to load
// the supplied source url. It returns a promise that resolves the
// image object.
function loadImage(url = '') {
  const image = new Image()
  return new Promise((resolve, reject) => {
    image.onload = () => {
      resolve(image)
    }

    image.onerror = e => {
      reject(e)
    }

    image.src = url
  })
}

// loadSvg attempts to load an svg image, and resizes it if necessary, returning the image object.
async function loadSvg(url = '', data) {
  const image = await loadImage(url)
  const dimensions = svgDefaultSizeIfUndefinedData(data, image)

  if (dimensions) {
    image.height = dimensions.height
    image.width = dimensions.width
  }

  return image
}

// isSvgRequest determines if an HTTP request contains an SVG content-type header.
function isSvgRequest(xhr) {
  return xhr.headers.get('content-type') === 'image/svg+xml'
}

/**
 * For our purposes, we consider an SVG to be 'valid' if it has
 * a valid xmlns namespace attribute.
 */
function isValidSvg(svg) {
  const xmlnsAddress = svg.getAttribute('xmlns')
  const hasNameSpace = Boolean(xmlnsAddress)

  if (!hasNameSpace) {
    return false
  }

  if (new URL(xmlnsAddress).protocol === 'http:') return true

  return false
}

/**
 * getSvgUrl returns either the srcUrl if the svg is valid, or a base64 encoded
 * url of the SVG contents.
 */
function getSvgUrl(svg, srcUrl) {
  if (isValidSvg(svg)) {
    return srcUrl
  }

  // svgs without a valid namespace will fail to load correctly
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')

  /**
   * This is the most reliable way to stringify the svg element
   * Calling .outerHTML on the node works in Chrome, but not in Firefox.
   * Likewise, serializing the node using an XMLSerializer object strips
   * the `xmlns` namespace attribute in Firefox.
   */
  const temp = document.createElement('p')
  temp.appendChild(svg)

  const svg64 = encodeURIComponent(temp.innerHTML)
  const b64Header = 'data:image/svg+xml;charset=utf-8,'

  return `${b64Header}${svg64}`
}

function svgDefaults() {
  // Still haven't given any default height & width? Lets give them arbitrary
  // values since SVGs are scalable and they gave us nothing to work with
  window.debug('Returning arbitrary SVG dimensions')
  return {height: DEFAULT_SIZE, width: DEFAULT_SIZE}
}

function svgDefaultSizeIfUndefinedData(svgDoc, image) {
  // When a document has no children, we know that the SVG didn't have a valid
  // namespace and was loaded from a base64 url. Therefore, we should either return
  // the browser-computed height and width of the image, or else the image size defaults.
  if (!svgDoc.children.length) {
    // Firefox likes to return huge image sizes that make no sense, so if its huge,
    // lets just ignore it and use another means of guessing the SVG size.
    const firefoxBuggySize = Math.pow(2, 32) - 1
    if (image.height > 1 && image.width > 1 && image.height < firefoxBuggySize && image.width < firefoxBuggySize) {
      window.debug(`Returning browser based SVG dimensions: ${image.height} by ${image.width}`)
      return {height: image.height, width: image.width}
    }

    return svgDefaults()
  }

  // SVGs may have a root element, if they are a document, or they may be an actual
  // instance of an svg node. Both of these conform to the Node interface.
  const maybeSvg = svgDoc?.rootElement ?? svgDoc

  // If we don't have a valid node, fall back to an empty object so checks below don't fail.
  const svg = maybeSvg instanceof Node ? maybeSvg : {}

  // If the SVG has a defined height & width that isn't a % we don't need to do
  // anything. Percentages can be explicitly passed in or computed when
  // specifying a viewBox
  const height = svg?.height?.baseVal
  const width = svg?.width?.baseVal

  if (
    height?.unitType !== SVGLength.SVG_LENGTHTYPE_PERCENTAGE &&
    width.unitType !== SVGLength.SVG_LENGTHTYPE_PERCENTAGE
  ) {
    return
  }

  // If the browser doesn't help us, lets try to determine the aspect ratio from
  // the viewbox and use an arbitrary height of 300px
  if ('hasAttribute' in svg && svg.hasAttribute('viewBox')) {
    const vb = svg.viewBox.baseVal
    const aspectRatio = vb.width / vb.height
    window.debug(`Returning viewbox based SVG dimensions with aspectRatio ${aspectRatio}`)
    return {height: DEFAULT_SIZE, width: DEFAULT_SIZE * aspectRatio}
  }

  return svgDefaults()
}

export {svgDefaultSizeIfUndefinedData, getSvgUrl, isSvgRequest, loadImage, loadSvg}
